import { FlyoutModal, onDesktop } from "@naf/teamscheme";
import { useEffect } from "react";
import styled, { css } from "styled-components";
import type { AccidentReportPage } from "#accident-statement/schema";
import ReportPreview, {
  useReportPreviewClient,
} from "../../../report/ReportPreview";
import { EVENTS } from "../../../tracking/EVENTS";
import { useTracking } from "../../../tracking/TrackingContext";
import { OutdatedPreviewMessage } from "./OutdatedPreviewMessage";
import { useHasOutdatedPreview } from "./useHasOutdatedPreview";

export interface PreviewModalProps {
  isOpen: boolean;
  onClose(): void;
  page: AccidentReportPage;
}

const PreviewMenuModal = styled(FlyoutModal)`
  width: 100%;
`;

const ModalReportPreview = styled(ReportPreview)`
  ${({ theme }) => css`
    width: auto;
    max-width: initial;
    margin: 0 -${theme.spacing.space24};

    ${onDesktop`
      margin: 0 -${theme.spacing.space48};
    `}

    .DocumentPreview {
      box-shadow: none;
      border: 1px solid ${theme.nafColor.neutral.neutral2};
    }
  `}
`;

function LoadDocumentPreview({
  page,
  isOpen,
}: {
  page: AccidentReportPage;
  isOpen: boolean;
}) {
  const context = useReportPreviewClient()[page];
  const hasOutdatedPreview = useHasOutdatedPreview(isOpen, context.isLoading);

  const { fetch: load } = context;
  useEffect(() => {
    if (isOpen) load();
  }, [isOpen, load]);

  const { event: trackEvent } = useTracking();
  useEffect(() => {
    if (isOpen) {
      trackEvent({ ...EVENTS.HEADER.PREVIEW, label: page });
    }
  }, [isOpen, trackEvent, page]);

  return (
    <>
      {hasOutdatedPreview ? <OutdatedPreviewMessage preview={context} /> : null}
      <ModalReportPreview preview={context} changed={hasOutdatedPreview} />
    </>
  );
}

export default function PreviewModal({
  isOpen,
  onClose,
  page,
}: PreviewModalProps) {
  return (
    <PreviewMenuModal isOpen={isOpen} onClose={onClose}>
      <h1>Forhåndsvisning</h1>
      <p>
        Dette er kun en forhåndsvisning og regnes ikke som gyldig dokument.
        Gyldig signert dokument sendes på e-post etter signering.
      </p>
      <LoadDocumentPreview page={page} isOpen={isOpen} />
    </PreviewMenuModal>
  );
}
